<template>
  <div class="newsletter-subscription-form">
    <div v-if="submitSuccess" class="newsletter-subscription__content">
      <transition appear name="fade">
        <div class="newsletter-subscription__error">
          <div class="newsletter-subscription__error-content">
            <RichTextRenderer
              v-if="thankYouMessage"
              :document="thankYouMessage"
            />
            <h3 v-else>Thank you!</h3>
          </div>
        </div>
      </transition>
    </div>

    <FormKit
      v-else
      :id="formId"
      v-slot="{ disabled }"
      v-model="values"
      :actions="false"
      name="subscription-popup-form"
      type="form"
      @submit="onFormSubmit"
    >
      <div
        class="newsletter-subscription__form-fields"
        :class="{ 'fields-stacked': stacked }"
      >
        <FormKit
          key="email"
          name="email"
          placeholder="Your email address"
          type="email"
          validation="required|email|length:1,50"
          outer-class="subscription-field email"
        />
        <FormKit
          key="name"
          name="FNAME"
          placeholder="Your first name"
          type="text"
          validation="required|length:2,24"
          outer-class="subscription-field name"
        />
        <FormKit
          :disabled="!!disabled"
          input-class="button button--lg"
          class="newsletter-subscription__button"
          type="submit"
        >
          <span> Submit </span>
          <i class="icon">
            <Icon
              :name="
                !!disabled
                  ? 'svg-spinners:90-ring-with-bg'
                  : 'fa6-solid:arrow-right'
              "
            />
          </i>
        </FormKit>
      </div>

      <transition
        name="form-messages"
        @after-enter="clearMessages"
        @before-enter="transitionBeforeEnter"
      >
        <div
          v-show="error"
          :id="'form_messages'"
          class="newsletter-subscription__messages"
        >
          <p v-if="error" class="error">
            {{ errorMessage }} <br />
            {{ error }}
          </p>
        </div>
      </transition>
    </FormKit>
    <CaptchaPolicy v-if="!submitSuccess" />
  </div>
</template>
<script setup lang="ts">
import type { FormData } from '#imports'

defineProps<{
  stacked?: boolean
}>()

const formId = ref('subscription-form')

const { values, error, transitionBeforeEnter, send, clearMessages } = useForm(
  '/api/forms/subscription',
  formId.value,
)

const { thankYouMessage, errorMessage } = useNewsletterContent()
const submitSuccess = ref(false)

const emit = defineEmits(['complete'])

async function onFormSubmit(data: FormData) {
  await send(data)

  if (error.value) {
    return
  } else {
    // Form submitted successfully
    submitSuccess.value = true
    emit('complete')
  }
}
</script>

<style lang="less" scoped>
.newsletter-subscription-form {
  margin: 0.75em 0 0;

  .newsletter-subscription__form-fields {
    display: flex;
    width: 100%;
    align-items: stretch;
    flex-wrap: wrap;
    gap: @gutter-xs;

    @media @desktop {
      flex-wrap: nowrap;
      gap: @gutter;
    }

    .subscription-field {
      margin: 0;
      flex-basis: 100%;

      &.name {
        flex: 1 1 50%;
      }

      @media @desktop {
        flex: 1 1 auto !important;
      }

      &[data-invalid='true'] {
        margin-bottom: 0;
      }

      * {
        height: 100%;
        max-height: @60px;
      }

      &[data-invalid='true'] {
        margin-bottom: @gutter-lg;
      }
    }

    :deep(input),
    :deep(button) {
      min-height: @48px;
      max-height: @48px !important;

      @media @desktop {
        min-height: @60px;
        max-height: @60px !important;
      }
    }

    button[type='submit'] {
      display: flex;
      align-items: center;
      .icon {
        margin-left: @9px;
      }
    }

    &.fields-stacked {
      flex-direction: row;
      flex-wrap: wrap;
      gap: @gutter-xs;

      .subscription-field {
        flex: 1 1 100% !important;

        &.name {
          flex: 50% !important;
          flex-shrink: 1 !important;
        }

        &[data-invalid='true'] {
          margin-bottom: 0;
        }
      }

      :deep(input),
      :deep(button) {
        min-height: @48px;
        max-height: @48px !important;
      }
    }
  }

  .newsletter-subscription__messages {
    transition: all 5s ease-in-out;

    .error {
      margin-top: @gutter;
      padding: @gutter-sm @gutter;
      background-color: @state-danger;
      color: @color-fill-normal;

      @media @desktop {
        margin-top: @gutter;
      }
    }
  }

  .newsletter-subscription__error {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .newsletter-subscription__error-content {
      flex: 1;
    }
  }
}
</style>
